import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DONT_QUIT_MESSAGES, PHASES } from '../utils/data';

export const TimerCircle = ({ elapsed, targetHours, isFasting }) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const [usedMessages, setUsedMessages] = useState([]);

  useEffect(() => {
    if (!isFasting) {
      setCurrentMessage('');
      setUsedMessages([]);
      return;
    }

    const rotateMessage = () => {
      let availableMessages = DONT_QUIT_MESSAGES.filter(m => !usedMessages.includes(m));
      if (availableMessages.length === 0) {
        availableMessages = DONT_QUIT_MESSAGES;
        setUsedMessages([]);
      }

      const randomMessage = availableMessages[Math.floor(Math.random() * availableMessages.length)];
      setCurrentMessage(randomMessage);
      setUsedMessages(prev => [...prev, randomMessage]);
    };

    rotateMessage();
    const interval = setInterval(rotateMessage, 12000);
    return () => clearInterval(interval);
  }, [isFasting, usedMessages]);

  const targetSeconds = targetHours * 3600;
  const progress = Math.min(elapsed / targetSeconds, 1);
  const radius = 135;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - progress * circumference;

  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const remaining = Math.max(targetSeconds - elapsed, 0);

  const { currentPhase, phaseProgress, phaseTimeRemaining } = useMemo(() => {
    const elapsedHours = elapsed / 3600;
    const phase = PHASES.find(p => elapsedHours >= p.start && elapsedHours < p.end) || PHASES[PHASES.length - 1];
    const phaseDuration = phase.end - phase.start;
    const progress = phaseDuration > 100 ? 100 : Math.min(((elapsedHours - phase.start) / phaseDuration) * 100, 100);
    const phaseRemainingHours = Math.max(phase.end - elapsedHours, 0);
    const phaseRemainingSeconds = phaseRemainingHours * 3600;

    return {
      currentPhase: phase,
      phaseProgress: progress,
      phaseTimeRemaining: phaseRemainingSeconds
    };
  }, [elapsed]);

  const formatPhaseTime = (totalSeconds) => {
    if (totalSeconds > 86400) {
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      return `${days}d ${hours}h`;
    }
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <div className="relative flex flex-col items-center justify-center py-8">
      {/* Timer Circle Container - Fixed positioning and size */}
      <div className="relative w-[320px] h-[320px] flex items-center justify-center flex-shrink-0">
        <svg className="absolute inset-0 transform -rotate-90 w-full h-full">
          <circle
            cx="160"
            cy="160"
            r={radius}
            stroke="currentColor"
            strokeWidth="10"
            fill="transparent"
            className="text-slate-100"
          />
          <circle
            cx="160"
            cy="160"
            r={radius}
            stroke="currentColor"
            strokeWidth="10"
            fill="transparent"
            strokeDasharray={circumference}
            strokeLinecap="round"
            className={`${progress >= 1 ? 'text-amber-400' : 'text-blue-500'}`}
            style={{
              strokeDashoffset: strokeDashoffset,
              transition: 'stroke-dashoffset 0.3s ease-out'
            }}
          />
        </svg>

        {/* Centered Timer Display - Fixed dimensions */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="text-slate-400 text-[10px] font-bold tracking-[0.2em] uppercase mb-2 h-4">
            {isFasting ? (progress >= 1 ? "GOAL REACHED" : "FASTING") : "READY"}
          </div>

          <div className="text-6xl font-black text-slate-900 tabular-nums tracking-tighter leading-none h-[72px] flex items-center">
            {formatTime(elapsed)}
          </div>

          {isFasting && (
            <div
              className={`mt-3 px-3 py-1 rounded-full text-[10px] font-bold border h-6 flex items-center ${
                progress >= 1
                  ? 'bg-amber-50 text-amber-600 border-amber-100'
                  : 'bg-orange-50 text-orange-600 border-orange-100'
              }`}
            >
              {progress >= 1
                ? `OVER BY ${formatTime(elapsed - targetSeconds)}`
                : `REMAINING ${formatTime(remaining)}`}
            </div>
          )}
        </div>
      </div>

      {/* Don't Quit Message & Phase Progress - Fixed container with stable height */}
      {isFasting && (
        <div className="mt-8 w-full max-w-sm space-y-4">
          {/* Fixed height container for message to prevent layout shift */}
          <div className="h-[88px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMessage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-4 rounded-2xl text-center font-bold text-sm leading-relaxed shadow-lg shadow-emerald-100 border border-emerald-400 w-full"
              >
                "{currentMessage}"
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Phase Progress Card - Fixed height */}
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 truncate flex-1 pr-2">
                Phase: {currentPhase.title}
              </div>
              <div className="text-emerald-500 text-[10px] font-black w-12 text-right flex-shrink-0">
                {Math.round(phaseProgress)}%
              </div>
            </div>

            <div className="h-2 bg-slate-50 rounded-full overflow-hidden border border-slate-100 p-0.5 mb-2">
              <div
                style={{ width: `${phaseProgress}%` }}
                className="h-full bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.3)] transition-all duration-300 ease-out"
              />
            </div>

            <div className="text-[10px] font-bold text-slate-500 text-center h-4 flex items-center justify-center">
              {phaseProgress >= 100
                ? 'Phase Complete'
                : `Next phase in ${formatPhaseTime(phaseTimeRemaining)}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};